import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type {AccountType} from "@hosttools/core/constant";
import {
    AddIcon,
    AlertCircleIcon,
    Box,
    Button,
    ButtonIcon,
    ButtonText,
    ChainIcon,
    ChannelLogo,
    HStack,
    Link,
    Pressable,
    RefreshIcon,
    Text,
    useChannel,
    VStack
} from "@hosttools/frontend";
import type React from "react";
import {memo, useCallback} from "react";

import type {ConnectedChannels} from "@/admin/components/ModalAddAccount/Channels";

type Props = {
    channel: AccountType;
    connectedChannels: ConnectedChannels;
    disconnectedChannels: ConnectedChannels;
    hideSocial?: boolean;
    onSelectChannel: (channel: AccountType) => void;
};

const containerStyle = tva({
    base: "p-4 flex-grow flex-shrink border border-solid border-blueGray-200 rounded-lg",
    variants: {
        hideSocial: {
            true: "w-40 flex-col",
            false: "w-full flex-row"
        }
    }
});

const stackStyle = tva({
    base: "justify-center items-center",
    variants: {
        hideSocial: {
            true: "flex-col",
            false: "flex-row"
        }
    }
});

const ChannelSelection: React.FC<Props> = ({
    channel,
    connectedChannels,
    disconnectedChannels,
    hideSocial,
    onSelectChannel
}) => {
    const numberOfConnected = connectedChannels[channel];
    const numberOfDisconnected = disconnectedChannels[channel];
    const {label} = useChannel(channel);

    const handleSelectChannel = useCallback(() => {
        onSelectChannel(channel);
    }, [channel, onSelectChannel]);

    return (
        <Box className={containerStyle({hideSocial})}>
            <Pressable
                role="presentation"
                testID={`${channel}-logo-wrapper`}
                onPress={handleSelectChannel}
            >
                <VStack className={stackStyle({hideSocial})} space="md">
                    {channel === "Seam" ? (
                        <Link
                            href="https://help.hosttools.com/en/articles/6066284-do-you-support-smart-locks"
                            type="external"
                        >
                            <ChannelLogo channel={channel} size="3xl" />
                        </Link>
                    ) : (
                        <ChannelLogo channel={channel} size="2xl" />
                    )}
                    <Box className={hideSocial ? "items-center" : "items-start"}>
                        <Text className="font-semibold">{label}</Text>
                        {!!numberOfConnected && !numberOfDisconnected && (
                            <HStack
                                className="bg-blueGray-50 py-0.5 px-1 rounded mt-1 items-center"
                                space="xs"
                            >
                                <ChainIcon color="blueGray.400" size="2xs" />
                                <Text variant="sm">+ {connectedChannels[channel]}</Text>
                            </HStack>
                        )}
                        {!!numberOfDisconnected && (
                            <HStack
                                className="bg-error-50 py-0.5 px-1 rounded items-center"
                                space="xs"
                            >
                                <AlertCircleIcon color="error.600" size="2xs" />
                                <Text color="error.600" variant="sm">
                                    + {disconnectedChannels[channel]}
                                </Text>
                            </HStack>
                        )}
                    </Box>
                </VStack>
            </Pressable>
            <Button
                data-testid={`${channel}-button-connect`}
                size="sm"
                variant="ghost"
                onPress={handleSelectChannel}
            >
                {numberOfDisconnected ? (
                    <>
                        <ButtonIcon as={RefreshIcon} size="sm" />
                        <ButtonText>Fix</ButtonText>
                    </>
                ) : (
                    <>
                        <ButtonIcon as={AddIcon} />
                        <ButtonText>Connect {numberOfConnected ? "More" : "Account"}</ButtonText>
                    </>
                )}
            </Button>
        </Box>
    );
};

export default memo(ChannelSelection);
